import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const expiryTimer = useRef(null);

  const clearSession = useCallback(() => {
    if (expiryTimer.current) window.clearTimeout(expiryTimer.current);
    localStorage.removeItem("botfull_token");
    setUser(null);
  }, []);

  const scheduleExpiry = useCallback((token) => {
    if (expiryTimer.current) window.clearTimeout(expiryTimer.current);
    try {
      const payloadPart = token.split(".")[1].replaceAll("-", "+").replaceAll("_", "/");
      const paddedPayload = payloadPart.padEnd(Math.ceil(payloadPart.length / 4) * 4, "=");
      const payload = JSON.parse(window.atob(paddedPayload));
      const delay = (Number(payload.exp) * 1000) - Date.now();
      if (!Number.isFinite(delay) || delay <= 0) {
        clearSession();
        return false;
      }
      expiryTimer.current = window.setTimeout(clearSession, delay);
      return true;
    } catch {
      clearSession();
      return false;
    }
  }, [clearSession]);

  useEffect(() => {
    const verify = async () => {
      const token = localStorage.getItem("botfull_token");
      if (!token) return setLoading(false);
      if (!scheduleExpiry(token)) return setLoading(false);
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        clearSession();
      } finally {
        setLoading(false);
      }
    };
    verify();
    window.addEventListener("botfull:unauthorized", clearSession);
    return () => {
      window.removeEventListener("botfull:unauthorized", clearSession);
      if (expiryTimer.current) window.clearTimeout(expiryTimer.current);
    };
  }, [clearSession, scheduleExpiry]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("botfull_token", data.token);
    scheduleExpiry(data.token);
    setUser(data.user);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch { clearSession(); return; }
    clearSession();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);